import React, { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Slider } from "@/components/ui/slider";
import { 
  Volume2, Mic, Zap, Heart, Shield, Flame, 
  Play, Save, RotateCcw, Settings, Lock, Crown
} from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

// Motivational Style Presets
const VOICE_STYLES = {
  coach: {
    id: 'coach',
    name: 'Coach',
    icon: Shield,
    color: 'text-blue-400 bg-blue-500/10 border-blue-500/30',
    description: 'Supportive and encouraging',
    phrases: {
      workoutStart: "Alright, let's get to work. You've got this!",
      setComplete: "Great set! Take your rest, you earned it.",
      finalPush: "Final stretch! Give me everything you've got!",
      waterBreak: "Good time for water. Hydration is key to performance.",
      milestone50: "Halfway there! You're doing great, keep it up!",
      milestone100: "Goal reached! Proud of you. That's how champions train!",
      workoutComplete: "Excellent workout! Way to show up and put in the work.",
      encouragement: "You're stronger than you think. Let's go!",
      countdown30: "30 seconds left. Stay focused.",
      countdown10: "Final 10! Push through!"
    }
  },
  drillSergeant: {
    id: 'drillSergeant',
    name: 'Drill Sergeant',
    icon: Flame,
    color: 'text-red-400 bg-red-500/10 border-red-500/30',
    description: 'Intense and demanding',
    phrases: {
      workoutStart: "Listen up! No excuses today. Let's move!",
      setComplete: "Set done. Don't get comfortable. Next one's coming!",
      finalPush: "This is where champions are made! Don't quit on me!",
      waterBreak: "Hydrate! A dehydrated soldier is a weak soldier!",
      milestone50: "Halfway? That's nothing! We're just getting started!",
      milestone100: "Target achieved! But we don't celebrate until the mission is complete!",
      workoutComplete: "Outstanding! That's the kind of effort I expect. Dismissed!",
      encouragement: "Pain is temporary. Quitting lasts forever. Move it!",
      countdown30: "30 seconds! No slacking!",
      countdown10: "10 seconds! Give me everything!"
    }
  },
  zen: {
    id: 'zen',
    name: 'Zen Master',
    icon: Heart,
    color: 'text-purple-400 bg-purple-500/10 border-purple-500/30',
    description: 'Calm and mindful',
    phrases: {
      workoutStart: "Begin with intention. Feel your body. Let's flow.",
      setComplete: "Well done. Breathe deeply. Honor your rest.",
      finalPush: "You are capable. Trust your body. Almost there.",
      waterBreak: "Pause. Hydrate mindfully. Water is life energy.",
      milestone50: "You've come so far. Acknowledge your progress.",
      milestone100: "Beautiful. You've honored your commitment to yourself.",
      workoutComplete: "Namaste. You showed up for yourself today. Be proud.",
      encouragement: "Each rep is a gift to your future self.",
      countdown30: "30 breaths remaining. Stay present.",
      countdown10: "10 moments. Be here now."
    }
  },
  hype: {
    id: 'hype',
    name: 'Hype Man',
    icon: Zap,
    color: 'text-yellow-400 bg-yellow-500/10 border-yellow-500/30',
    description: 'High energy and exciting',
    phrases: {
      workoutStart: "YOOOO let's GOOO! Time to get AFTER IT!",
      setComplete: "BOOM! Crushed it! You're on FIRE today!",
      finalPush: "THIS IS IT! The moment of GLORY! SEND IT!",
      waterBreak: "HYDRATION STATION! Fuel up that BEAST MODE!",
      milestone50: "HALFWAY BABY! You're absolutely KILLING it!",
      milestone100: "GALLON GANG! You're a HYDRATION CHAMPION!",
      workoutComplete: "YOOO that was INSANE! You're a LEGEND! Let's GOOO!",
      encouragement: "You're built DIFFERENT! No one can stop you!",
      countdown30: "30 seconds of PURE GREATNESS!",
      countdown10: "TEN! NINE! EIGHT! Let's GOOO!"
    }
  }
};

// Default voice settings
const DEFAULT_SETTINGS = {
  voiceStyle: 'coach',
  voiceGender: 'female', // 'male' or 'female'
  speechRate: 1.0, // 0.5 to 2.0
  speechPitch: 1.0, // 0.5 to 1.5
  speechVolume: 0.8, // 0 to 1
  enabled: true
};

// Which tier unlocks which styles (for UI labels)
const STYLE_TIER_LABELS = {
  coach: null, // free for everyone
  drillSergeant: 'Pro',
  zen: 'Pro',
  hype: 'Pro'
};

const VoiceSettings = ({ 
  onSave = () => {},
  onClose = () => {},
  initialSettings = null,
  compact = false,
  allowedStyles = null // array of allowed style IDs from feature-access, null = all unlocked
}) => {
  const navigate = useNavigate();
  const [settings, setSettings] = useState(initialSettings || DEFAULT_SETTINGS);
  const [hasChanges, setHasChanges] = useState(false);
  const [availableVoices, setAvailableVoices] = useState([]);
  const [selectedVoice, setSelectedVoice] = useState(null);

  // Check if speech synthesis is available
  const [speechAvailable, setSpeechAvailable] = useState(false);

  const isStyleLocked = (styleId) => {
    if (!allowedStyles) return false; // no gating info = all unlocked
    return !allowedStyles.includes(styleId);
  };

  // Load available voices
  useEffect(() => {
    // Check if speechSynthesis is available
    if (typeof window === 'undefined' || !window.speechSynthesis) {
      setSpeechAvailable(false);
      return;
    }
    
    setSpeechAvailable(true);
    
    const loadVoices = () => {
      if (!window.speechSynthesis) return;
      const voices = window.speechSynthesis.getVoices();
      setAvailableVoices(voices);
      
      // Auto-select a voice based on gender preference
      selectVoiceByGender(settings.voiceGender, voices);
    };

    loadVoices();
    
    if (window.speechSynthesis.onvoiceschanged !== undefined) {
      window.speechSynthesis.onvoiceschanged = loadVoices;
    }

    // Load saved settings
    const saved = localStorage.getItem('voiceSettings');
    if (saved && !initialSettings) {
      const parsed = JSON.parse(saved);
      setSettings(parsed);
    }

    return () => {
      if (window.speechSynthesis) window.speechSynthesis.onvoiceschanged = null;
    };
  }, [initialSettings]);

  const selectVoiceByGender = (gender, voices = availableVoices) => {
    if (voices.length === 0) return;

    // Common female voice names
    const femaleNames = ['samantha', 'victoria', 'karen', 'moira', 'tessa', 'fiona', 'female', 'woman', 'zira', 'hazel'];
    // Common male voice names
    const maleNames = ['daniel', 'alex', 'tom', 'fred', 'male', 'man', 'david', 'james', 'mark'];

    const targetNames = gender === 'female' ? femaleNames : maleNames;
    
    let found = voices.find(v => 
      targetNames.some(name => v.name.toLowerCase().includes(name))
    );

    // Fallback to any English voice
    if (!found) {
      found = voices.find(v => v.lang.startsWith('en'));
    }

    // Last resort: first available
    if (!found && voices.length > 0) {
      found = voices[0];
    }

    setSelectedVoice(found);
  };

  const updateSetting = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
    setHasChanges(true);

    if (key === 'voiceGender') {
      selectVoiceByGender(value);
    }
  };

  const handleSave = () => {
    localStorage.setItem('voiceSettings', JSON.stringify(settings));
    onSave(settings);
    setHasChanges(false);
    toast.success("Voice settings saved!");
  };

  const handleReset = () => {
    setSettings(DEFAULT_SETTINGS);
    selectVoiceByGender(DEFAULT_SETTINGS.voiceGender);
    setHasChanges(true);
    toast.info("Settings reset to defaults");
  };

  const previewVoice = (phrase = null) => {
    if (!speechAvailable || !window.speechSynthesis) {
      toast.error("Voice not available on this device");
      return;
    }
    
    window.speechSynthesis.cancel();
    
    const style = VOICE_STYLES[settings.voiceStyle];
    const text = phrase || style.phrases.encouragement;
    
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.rate = settings.speechRate;
    utterance.pitch = settings.speechPitch;
    utterance.volume = settings.speechVolume;
    
    if (selectedVoice) {
      utterance.voice = selectedVoice;
    }
    
    window.speechSynthesis.speak(utterance);
  };

  const currentStyle = VOICE_STYLES[settings.voiceStyle];
  const StyleIcon = currentStyle.icon;

  if (compact) {
    return (
      <div className="flex items-center gap-2">
        <Button
          size="sm"
          variant="outline"
          onClick={() => previewVoice()}
          className="border-zinc-600"
        >
          <Play className="w-3 h-3 mr-1" />
          Test
        </Button>
        <Badge className={currentStyle.color}>
          <StyleIcon className="w-3 h-3 mr-1" />
          {currentStyle.name}
        </Badge>
      </div>
    );
  }

  return (
    <Card className="bg-gradient-to-br from-zinc-900 to-zinc-950 border-zinc-700">
      <CardContent className="p-5">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary/20 border border-primary/30 flex items-center justify-center">
              <Mic className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h3 className="font-bold text-white">Voice Coach Settings</h3>
              <p className="text-xs text-muted-foreground">Customize your workout voice</p>
            </div>
          </div>
          {hasChanges && (
            <Badge className="bg-yellow-500/20 text-yellow-400">Unsaved</Badge>
          )}
        </div>

        <div className="space-y-6">
          {/* Voice Style Selection */}
          <div className="space-y-3">
            <label className="text-sm font-medium text-white flex items-center gap-2">
              <Zap className="w-4 h-4 text-yellow-400" />
              Coaching Style
            </label>
            <div className="grid grid-cols-2 gap-2">
              {Object.values(VOICE_STYLES).map((style) => {
                const Icon = style.icon;
                const isSelected = settings.voiceStyle === style.id;
                const locked = isStyleLocked(style.id);
                const tierLabel = STYLE_TIER_LABELS[style.id];
                return (
                  <button
                    key={style.id}
                    data-testid={`voice-style-${style.id}`}
                    onClick={() => {
                      if (locked) {
                        toast.error(`${style.name} style requires a ${tierLabel} subscription`, {
                          action: {
                            label: 'Upgrade',
                            onClick: () => navigate('/subscription')
                          }
                        });
                        return;
                      }
                      updateSetting('voiceStyle', style.id);
                    }}
                    className={`p-3 rounded-lg border transition-all text-left relative ${
                      locked
                        ? 'bg-zinc-800/30 border-zinc-700/50 opacity-60 cursor-not-allowed'
                        : isSelected 
                          ? `${style.color} ring-2 ring-offset-2 ring-offset-zinc-900` 
                          : 'bg-zinc-800/50 border-zinc-700 hover:border-zinc-600'
                    }`}
                  >
                    {locked && (
                      <div className="absolute top-2 right-2 flex items-center gap-1">
                        <Lock className="w-3 h-3 text-amber-400" />
                      </div>
                    )}
                    <div className="flex items-center gap-2 mb-1">
                      <Icon className={`w-4 h-4 ${locked ? 'text-zinc-500' : isSelected ? '' : 'text-zinc-400'}`} />
                      <span className={`font-medium text-sm ${locked ? 'text-zinc-500' : isSelected ? '' : 'text-white'}`}>
                        {style.name}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground">{style.description}</p>
                    {locked && tierLabel && (
                      <Badge className="mt-1.5 bg-amber-500/10 text-amber-400 border-amber-500/20 text-[10px] px-1.5 py-0">
                        <Crown className="w-2.5 h-2.5 mr-0.5" />{tierLabel}+
                      </Badge>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Voice Gender */}
          <div className="space-y-3">
            <label className="text-sm font-medium text-white flex items-center gap-2">
              <Volume2 className="w-4 h-4 text-blue-400" />
              Voice Type
            </label>
            <div className="flex gap-2">
              <Button
                onClick={() => updateSetting('voiceGender', 'female')}
                variant={settings.voiceGender === 'female' ? 'default' : 'outline'}
                className={settings.voiceGender === 'female' ? 'bg-pink-600 hover:bg-pink-500' : ''}
              >
                Female
              </Button>
              <Button
                onClick={() => updateSetting('voiceGender', 'male')}
                variant={settings.voiceGender === 'male' ? 'default' : 'outline'}
                className={settings.voiceGender === 'male' ? 'bg-blue-600 hover:bg-blue-500' : ''}
              >
                Male
              </Button>
            </div>
            {selectedVoice && (
              <p className="text-xs text-muted-foreground">
                Using: {selectedVoice.name}
              </p>
            )}
          </div>

          {/* Speech Rate */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-white">Speech Speed</label>
              <Badge variant="outline">{settings.speechRate.toFixed(1)}x</Badge>
            </div>
            <Slider
              value={[settings.speechRate]}
              onValueChange={([value]) => updateSetting('speechRate', value)}
              min={0.5}
              max={2.0}
              step={0.1}
              className="w-full"
            />
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>Slow (0.5x)</span>
              <span>Normal (1x)</span>
              <span>Fast (2x)</span>
            </div>
          </div>

          {/* Speech Pitch */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-white">Voice Pitch</label>
              <Badge variant="outline">{settings.speechPitch.toFixed(1)}</Badge>
            </div>
            <Slider
              value={[settings.speechPitch]}
              onValueChange={([value]) => updateSetting('speechPitch', value)}
              min={0.5}
              max={1.5}
              step={0.1}
              className="w-full"
            />
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>Deep</span>
              <span>Normal</span>
              <span>High</span>
            </div>
          </div>

          {/* Volume */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-white">Volume</label>
              <Badge variant="outline">{Math.round(settings.speechVolume * 100)}%</Badge>
            </div>
            <Slider
              value={[settings.speechVolume]}
              onValueChange={([value]) => updateSetting('speechVolume', value)}
              min={0}
              max={1}
              step={0.1}
              className="w-full"
            />
          </div>

          {/* Preview Buttons */}
          <div className="space-y-2 pt-4 border-t border-zinc-800">
            <p className="text-xs text-muted-foreground mb-2">Preview voice:</p>
            <div className="flex gap-2 flex-wrap">
              <Button
                size="sm"
                variant="outline"
                onClick={() => previewVoice(currentStyle.phrases.workoutStart)}
              >
                <Play className="w-3 h-3 mr-1" />
                Workout Start
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => previewVoice(currentStyle.phrases.encouragement)}
              >
                <Play className="w-3 h-3 mr-1" />
                Encouragement
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => previewVoice(currentStyle.phrases.waterBreak)}
              >
                <Play className="w-3 h-3 mr-1" />
                Water Break
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => previewVoice(currentStyle.phrases.finalPush)}
              >
                <Play className="w-3 h-3 mr-1" />
                Final Push
              </Button>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3 mt-6 pt-4 border-t border-zinc-800">
          <Button
            onClick={handleReset}
            variant="outline"
            className="flex-1"
          >
            <RotateCcw className="w-4 h-4 mr-2" />
            Reset
          </Button>
          <Button
            onClick={handleSave}
            className="flex-1 bg-primary hover:bg-primary/90"
            disabled={!hasChanges}
          >
            <Save className="w-4 h-4 mr-2" />
            Save Settings
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

// Hook to use voice settings
export const useVoiceCoach = (allowedStyles = null) => {
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [selectedVoice, setSelectedVoice] = useState(null);
  const [speechAvailable, setSpeechAvailable] = useState(false);

  useEffect(() => {
    // Check if speechSynthesis is available
    if (typeof window === 'undefined' || !window.speechSynthesis) {
      setSpeechAvailable(false);
      return;
    }
    
    setSpeechAvailable(true);
    
    // Load saved settings
    const saved = localStorage.getItem('voiceSettings');
    if (saved) {
      const parsed = JSON.parse(saved);
      // If saved style isn't allowed, fall back to coach
      if (allowedStyles && !allowedStyles.includes(parsed.voiceStyle)) {
        parsed.voiceStyle = 'coach';
      }
      setSettings(parsed);
    }

    // Load voices
    const loadVoices = () => {
      if (!window.speechSynthesis) return;
      const voices = window.speechSynthesis.getVoices();
      const gender = saved ? JSON.parse(saved).voiceGender : 'female';
      
      const femaleNames = ['samantha', 'victoria', 'karen', 'female', 'woman', 'zira'];
      const maleNames = ['daniel', 'alex', 'tom', 'male', 'man', 'david'];
      const targetNames = gender === 'female' ? femaleNames : maleNames;
      
      let found = voices.find(v => 
        targetNames.some(name => v.name.toLowerCase().includes(name))
      );
      if (!found) found = voices.find(v => v.lang.startsWith('en'));
      if (!found && voices.length > 0) found = voices[0];
      
      setSelectedVoice(found);
    };

    loadVoices();
    
    if (window.speechSynthesis.onvoiceschanged !== undefined) {
      window.speechSynthesis.onvoiceschanged = loadVoices;
    }

    return () => {
      if (window.speechSynthesis && window.speechSynthesis.onvoiceschanged !== undefined) {
        window.speechSynthesis.onvoiceschanged = null;
      }
    };
  }, []);

  const speak = (phraseKey, customText = null) => {
    if (!settings.enabled || !speechAvailable || !window.speechSynthesis) return;
    
    window.speechSynthesis.cancel();
    
    const style = VOICE_STYLES[settings.voiceStyle];
    const text = customText || style.phrases[phraseKey] || phraseKey;
    
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.rate = settings.speechRate;
    utterance.pitch = settings.speechPitch;
    utterance.volume = settings.speechVolume;
    
    if (selectedVoice) {
      utterance.voice = selectedVoice;
    }
    
    window.speechSynthesis.speak(utterance);
  };

  const getPhrase = (phraseKey) => {
    const style = VOICE_STYLES[settings.voiceStyle];
    return style.phrases[phraseKey] || '';
  };

  return { speak, getPhrase, settings, VOICE_STYLES, speechAvailable };
};

export { VOICE_STYLES, DEFAULT_SETTINGS };
export default VoiceSettings;
